import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar/Sidebar';
import Header from './components/Header/Header';
import ChangePasswordModal from './components/ChangePasswordModal/ChangePasswordModal';
import LandingPage from './pages/LandingPage/LandingPage';
import Login from './pages/Login/Login';
import Home from './pages/Home/Home';
import Katalog from './pages/Katalog/Katalog';
import BookDetail from './pages/BookDetail/BookDetail';
import Peminjaman from './pages/Peminjaman/Peminjaman';
import Jurnal from './pages/Jurnal/Jurnal';
import JurnalDosen from './pages/JurnalDosen/JurnalDosen';
import BukuSaya from './pages/BukuSaya/BukuSaya';
import Sitasi from './pages/Sitasi/Sitasi';
import Profil from './pages/Profil/Profil';
import KatalogPublic from './pages/KatalogPublic/KatalogPublic';
import BookDetailPublic from './pages/KatalogPublic/BookDetailPublic';
import './styles/index.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Memuat...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Main App Content with Layout
const AppContent = () => {
  const { isAuthenticated, user, updateUser } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Check if user needs to change password
  useEffect(() => {
    if (user?.must_change_password) {
      setShowChangePassword(true);
    }
  }, [user]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleMobileSidebar = () => setMobileSidebarOpen(!mobileSidebarOpen);
  const closeMobileSidebar = () => setMobileSidebarOpen(false);

  const handlePasswordChanged = () => {
    // Update user to remove must_change_password flag
    updateUser({ ...user, must_change_password: false });
    setShowChangePassword(false);
  };

  // Auth pages don't need layout
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/katalog-publik" element={<KatalogPublic />} />
        <Route path="/katalog-publik/:id" element={<BookDetailPublic />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className={`app ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${mobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}>
      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal 
          user={user}
          onSuccess={handlePasswordChanged}
          onClose={() => {}}
        />
      )}

      {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div className="mobile-overlay" onClick={closeMobileSidebar} />
      )}
      
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={toggleSidebar}
        isMobileOpen={mobileSidebarOpen}
      />
      
      <main className="app__main">
        <Header 
          toggleMobileSidebar={toggleMobileSidebar}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
        />
        <div className="app__content">
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/katalog" element={<ProtectedRoute><Katalog /></ProtectedRoute>} />
            <Route path="/katalog/:id" element={<ProtectedRoute><BookDetail /></ProtectedRoute>} />
            <Route path="/peminjaman" element={<ProtectedRoute><Peminjaman /></ProtectedRoute>} />
            <Route path="/jurnal" element={<ProtectedRoute><Jurnal /></ProtectedRoute>} />
            <Route path="/jurnal-dosen" element={<ProtectedRoute><JurnalDosen /></ProtectedRoute>} />
            <Route path="/buku-saya" element={<ProtectedRoute><BukuSaya /></ProtectedRoute>} />
            <Route path="/sitasi" element={<ProtectedRoute><Sitasi /></ProtectedRoute>} />
            <Route path="/profil" element={<ProtectedRoute><Profil /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
